import React, { FC, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { ControlGroupLabelSection } from './ControlGroupLabelSection';
import classes from './styles/index.module.scss';
import { ColumnsLayoutItemInUseContext } from '../../../modules/detailPage/components/ColumnsLayout/const';
import { ControlGroupSectionItemInUseContext } from '../ControlGroupSection/const';
import { ControlGroupValidationSection } from './ControlGroupValidationSection';
import StringUtils from '../../../utils/string';
import Type from '../../../utils/type';
import { getLabelPositionClass } from './utils';
import { ControlGroupProps } from '../types';

export const ControlGroup: FC<React.PropsWithChildren<ControlGroupProps>> = (props) => {
	const {
		children,
		className,
		componentClassName,
		componentAfterChildrenClassName,
		inline,
		isRequired,
		hiddenLabel,
		labelBeforeComponent,
		beforeChildren,
		afterChildren,
		afterLabel,
		position,
		controlGroupClassName,
		previewMode,
		dataTestId,
		label,
	} = props;
	const { hasParentColumnsLayoutItem } = useContext(ColumnsLayoutItemInUseContext);
	const { hasParentControlGroupSectionItem } = useContext(ControlGroupSectionItemInUseContext);
	const hasTip = !!props.tipMessage;
	const hasAfterLabel = !Type.isNull(afterLabel);
	const hasBottomPadding = !previewMode && hasParentColumnsLayoutItem && !hasParentControlGroupSectionItem;
	const controlGroupClasses = classNames(
		classes.controlGroup,
		controlGroupClassName,
		getLabelPositionClass(position),
		className,
		{
			[classes.controlGroup_inline]: inline,
			[classes.controlGroup_required]: isRequired,
			//[classes.controlGroup_hiddenLabel]: hiddenLabel,
			[classes.controlGroup_labelBeforeComponent]: labelBeforeComponent,
			[classes.controlGroup_hasBottomPaddingInEditMode]: hasBottomPadding,
		},
		'reactControlGroup',
	);
	const componentClasses = classNames(classes.component, className, componentClassName, {
		[classes.component_flexy]: beforeChildren || afterChildren,
	});
	const componentAfterChildrenClasses = classNames(classes.componentAfterChildren, componentAfterChildrenClassName);

	const hasLabel = !StringUtils.isNullOrEmpty(label);

	const ariaLabelProps = useMemo(() => {
		if (!hasLabel || typeof label !== 'string') {
			return {};
		}

		return hiddenLabel ? { 'aria-labelledby': label } : { 'aria-label': label };
	}, [hasLabel, hiddenLabel, label]);

	const labelSection = hasLabel && !hiddenLabel && (
		<ControlGroupLabelSection
			{...props}
			hasTip={hasTip}
			hasAfterLabel={hasAfterLabel}
		/>
	);

	return (
		<div
			className={controlGroupClasses}
			data-testid={dataTestId}
			{...ariaLabelProps}
		>
			{position !== 'right' && labelSection}
			<div
				className={componentClasses}
				data-testid='controlGroup__component'
			>
				{beforeChildren && (
					<div
						className={classes.componentBeforeChildren}
						data-testid='controlGroup__beforeChildren'
					>
						{beforeChildren}
					</div>
				)}
				<ControlGroupValidationSection {...props}>{children}</ControlGroupValidationSection>
				{afterChildren && (
					<div
						className={componentAfterChildrenClasses}
						data-testid='controlGroup__afterChildren'
					>
						{afterChildren}
					</div>
				)}
			</div>
			{position === 'right' && labelSection}
		</div>
	);
};

ControlGroup.defaultProps = {
	className: '',
	label: '',
	afterLabel: null,
	inline: false,
	isRequired: false,
	hiddenLabel: false,
	labelBold: false,
	labelWhite: false,
	validationMessagePlace: 'component',
	valid: true,
	tipMessage: '',
	mobileValidationInTooltip: false,
	labelBeforeComponent: false,
	hideValidationTooltip: false,
	previewMode: false,
};

export default ControlGroup;
